import React, { useEffect, useState } from 'react';
import { Box, Container } from '@mui/material';
import StyledBox from 'views/experience-module/MotorcycleTripView/MotorcycleTripView.styles';
import {
  MotorcycleTripHeaderSection,
  ComparisonSection,
  MotorcycleToursSection,
  MotorcycleTripsSection,
  MotorcycleTripHeaderSectionSkeleton,
  MotorcycleToursSectionSkeleton,
  ComparisonSectionSkeleton,
  MotorcycleTripsSectionSkeleton,
  MotorcycleRentalsSection,
  MotorcycleRentalsSectionSkeleton,
} from 'views/experience-module/MotorcycleTripView/components';
import { LazyLoad } from 'views/common/components';

import { API } from 'lib/config/axios';
import generateMetaData from 'lib/utils/generateMetaData';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import Head from 'next/head';

import ScrollTo from 'lib/utils/ScrollTo';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import generateOneSlugHreflangs from 'views/common/utils/generateOneSlugHreflangs';
import { setHrefLangData } from 'redux/features/globalData-slice';
import { useDispatch, useSelector } from 'react-redux';
import useRenderAiBot from 'views/common/hooks/useRenderAiBot';
import useGetLayout from 'views/common/hooks/useGetLayout';
import { PAGES_GA_IDENTIFIERS } from 'views/common/utils/constants';

const MainWidgetDialog = dynamic(() => import('views/common/components/UI/MainWidgetDialog').then(
  (file) => file.MainWidgetDialog,
));
const MotorcycleTripView = (props) => {
  const { t } = useTranslation('fe_er_motorcycle_trip_page');
  const { serverData, slugData } = props;
  const [data, setData] = useState(serverData);
  const { hreflangData, isSSR } = useSelector((state) => state.globalData);
  useRenderAiBot(true);

  const { getPageData } = useGetLayout();
  const appNavigator = useAppNavigator();
  const dispatch = useDispatch();
  const handleErrorResponse = useErrorsHandler();
  const [openMainWidgetDialog, setOpenMainWidgetDialog] = useState(false);
  const handleCloseMainWidgetDialog = () => {
    setOpenMainWidgetDialog(false);
  };

  const getData = async () => {
    try {
      const [, res] = await getPageData(
        API.motorcycleTrips.id.replace('{id}', slugData.slugs[0].resource_id),
      );
      const hrefLangs = generateOneSlugHreflangs(
        appNavigator.asPath,
        appNavigator.locale,
        res.data.experience_page?.slug_translations,
      );
      dispatch(setHrefLangData(hrefLangs));
      setData(res.data);
    } catch (error) {
      handleErrorResponse(error, false);
    }
  };

  useEffect(() => {
    if (!isSSR) {
      getData();
    }
  }, []);

  useEffect(() => {
    if (isSSR) {
      setData(serverData);
    }
    window.gtag('event', 'landing_page_view', {
      page_identifier: PAGES_GA_IDENTIFIERS.EXPERIENCE_LANDING,
    });
  }, [serverData]);

  const hanleBookNowBtnClick = (btnType) => {
    switch (btnType) {
      case t('fe_er_motorcycle_trip_page:bike_only'):
        setOpenMainWidgetDialog(true);
        break;
      case t('fe_er_motorcycle_trip_page:self_drive'):
        ScrollTo('selfDriveTours');
        break;
      case t('fe_er_motorcycle_trip_page:guided'):
        ScrollTo('guidedTours');
        break;
      default:
        break;
    }
  };

  const getVehicleModels = () => {
    const vehicleModels = [];
    if (serverData) {
      data.promoted_vehicle_makes.forEach((vm) => {
        if (vm.vehicle_models.length > 0) {
          vehicleModels.push(...vm.vehicle_models);
        }
      });
      const copiedData = data;
      copiedData.promoted_vehicle_makes[0].vehicle_models = vehicleModels;
      return copiedData.promoted_vehicle_makes;
    }
    return data?.promoted_vehicle_makes;
  };

  return (
    <>
      <Box className="overFlowXHidden">
        {data?.seo_values && (
          <Head>
            {generateMetaData(data?.seo_values)}
            {' '}
            {hreflangData && !serverData && (
              <>
                <link rel="canonical" href={hreflangData?.canonical} />
                {hreflangData.urls?.map((local) => (
                  <link
                    key={local.hreflang}
                    rel="alternate"
                    href={local.url}
                    hrefLang={local.hreflang}
                  />
                ))}
              </>
            )}
          </Head>
        )}
        {data ? (
          <MotorcycleTripHeaderSection
            tripName={data?.experience_page.name}
            tripImg={data?.experience_page.cover_image}
          />
        ) : (
          <MotorcycleTripHeaderSectionSkeleton />
        )}

        <StyledBox>
          {data ? (
            <Box className="MTVSectionSpace" id="comparisonSection">
              <Box className="MTVcomparisonSection">
                <ComparisonSection
                  tripName={data?.experience_page.name}
                  tripImg={data?.experience_page.cover_image.large_url}
                  hanleBookNowBtnClick={hanleBookNowBtnClick}
                  trips={data}
                />
              </Box>
            </Box>
          ) : (
            <ComparisonSectionSkeleton />
          )}
          <Container maxWidth="xl" className="MTVContainer">
            {data?.guided_tours.length > 0 && (
              <Box className="MTVSectionSpace" id="guidedTours">
                <LazyLoad
                  skeleton={<MotorcycleToursSectionSkeleton />}
                  dataLoaded={data}>
                  <MotorcycleToursSection
                    title={t('fe_er_motorcycle_trip_page:guided_tour_name', {
                      page_name: data?.experience_page.name,
                    })}
                    description={t(
                      'fe_er_motorcycle_trip_page:the_ultimate_experience',
                    )}
                    tourCardItems={data.guided_tours}
                  />
                </LazyLoad>
              </Box>
            )}
            {data?.self_drive_tours.length > 0 && (
              <Box className="MTVSectionSpace" id="selfDriveTours">
                <LazyLoad
                  skeleton={<MotorcycleToursSectionSkeleton />}
                  dataLoaded={data}>
                  <MotorcycleToursSection
                    title={t(
                      'fe_er_motorcycle_trip_page:self_guided_tour_name',
                      {
                        page_name: data?.experience_page.name,
                      },
                    )}
                    description={t(
                      'fe_er_motorcycle_trip_page:Dream_it_we_plan_it',
                    )}
                    tourCardItems={data.self_drive_tours}
                  />
                </LazyLoad>
              </Box>
            )}

            <Box className="MTVSectionSpace">
              <LazyLoad
                skeleton={<MotorcycleRentalsSectionSkeleton />}
                dataLoaded={data}>
                <MotorcycleRentalsSection
                  title={t('fe_er_motorcycle_trip_page:motorcycle_rentals', {
                    page_name: data?.experience_page.name,
                  })}
                  description={t(
                    'fe_er_motorcycle_trip_page:available_models',
                    {
                      available_model: data?.promoted_bikes_total_count,
                      page_name: data?.experience_page.name,
                    },
                  )}
                  motorcycleRentals={getVehicleModels()}
                />
              </LazyLoad>
            </Box>
            <Box className="MTVSectionSpace">
              <LazyLoad
                skeleton={<MotorcycleTripsSectionSkeleton />}
                dataLoaded={data}>
                <MotorcycleTripsSection
                  experiencePageData={data?.experience_page}
                />
              </LazyLoad>
            </Box>
            {openMainWidgetDialog && (
              <MainWidgetDialog
                open={openMainWidgetDialog}
                handleClose={handleCloseMainWidgetDialog}
                reservationData={{
                  pickup_location_id:
                    data?.experience_page?.default_location_id,
                  dropoff_location_id:
                    data?.experience_page?.default_location_id,
                }}
                floatingDialog
                renderFullWidgetByForce
                fixPosition
              />
            )}
          </Container>
        </StyledBox>
      </Box>
    </>
  );
};

export { MotorcycleTripView };
